"use client";

import { ChevronRight, Maximize2, Star } from "lucide-react";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import React, {
	type CSSProperties,
	useCallback,
	useEffect,
	useState,
} from "react";
import useSWR from "swr";
import DemoFormBuilder from "@/components/form-builder/form-builder/demo-form-builder";
import { Badge, Card } from "../ui";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";

interface EmbeddedFormProps {
	className?: string;
	style?: CSSProperties;
}

const IFRAME_ORIGIN = "https://forms.projme.ru";
const DEFAULT_IFRAME_HEIGHT = 850;

export const EmbeddedForm = React.memo(function EmbeddedForm({
	className,
	style,
}: EmbeddedFormProps) {
	const [iframeHeight, setIframeHeight] = useState(DEFAULT_IFRAME_HEIGHT);
	const [hasError, setHasError] = useState(false);

	const handleMessage = useCallback((event: MessageEvent) => {
		if (event.origin !== IFRAME_ORIGIN) return;
		if (
			event.data?.type === "resize" &&
			typeof event.data?.height === "number"
		) {
			setIframeHeight(event.data.height);
		}
	}, []);

	React.useEffect(() => {
		window.addEventListener("message", handleMessage);
		return () => window.removeEventListener("message", handleMessage);
	}, [handleMessage]);

	const iframeStyle = React.useMemo<CSSProperties>(
		() => ({
			width: "100%",
			height: `${iframeHeight}px`,
			border: "1px solid #ffffff",
			borderRadius: "22px",
			display: "block",
			margin: "0 auto",
			...style,
		}),
		[iframeHeight, style]
	);

	if (hasError) {
		return (
			<div
				aria-live="polite"
				className="flex w-full flex-col items-center justify-center gap-4 rounded-lg border border-destructive/20 bg-destructive/5 p-8 text-center"
				role="alert"
			>
				<p className="font-medium text-destructive">
					Unable to load form demo. Please try refreshing the page.
				</p>
				<Button
					onClick={() => {
						setHasError(false);
						window.location.reload();
					}}
					size="sm"
					variant="outline"
				>
					Reload
				</Button>
			</div>
		);
	}

	return (
		<div className={`flex w-full justify-center ${className || ""}`}>
			<iframe
				allow="clipboard-write; camera; microphone"
				frameBorder="0"
				loading="lazy"
				onError={() => setHasError(true)}
				referrerPolicy="no-referrer"
				sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
				src="https://forms.projme.ru/f/form-0gqpeq"
				style={iframeStyle}
				title="forms.projme demo form"
			/>
		</div>
	);
});

EmbeddedForm.displayName = "EmbeddedForm";

function HeroHeading() {
	const t = useTranslations("home.hero");
	return (
		<h1
			className="text-center font-semibold text-4xl leading-tighter tracking-[-2px] md:text-5xl"
			id="home-hero-title"
		>
			{t("heading")}
		</h1>
	);
}

function HeroSubheading() {
	const t = useTranslations("home.hero");
	return (
		<p
			className="mx-auto max-w-2xl font-normal text-base leading-loose opacity-70 md:text-lg"
			id="home-hero-desc"
		>
			{t("subheading")}
		</p>
	);
}

interface UserStats {
	count: number | null;
	users: Array<{ initials: string }>;
}

const USER_STATS_FALLBACK: UserStats = { count: null, users: [] };
const userStatsFetcher = async (url: string): Promise<UserStats> => {
	const response = await fetch(url);
	if (!response.ok) {
		throw new Error("Failed to fetch user stats");
	}
	return response.json();
};

const GRAVATAR_URLS = [
	"https://www.gravatar.com/avatar/820117d4b7a01756fc47e0075f0cb6c01a82691ad3ec3ce9a1f49235d0c672ed?d=404",
	"https://www.gravatar.com/avatar/cb0434f4899c68c4bd364f67403cfba7336d1fd1b8a83e15b529b69c91296248?d=404",
	"https://www.gravatar.com/avatar/529f268c1c943e596df18bc22e4f42af5a2cf69e4f0a411d9412847a10aa8a45?d=404",
	"https://www.gravatar.com/avatar/3a5180d835aca06fe8782f611176f1d6064d8ce7852b1e2844be833e03df4cb9?d=404",
];

function AvatarGroup() {
	const { data: stats = USER_STATS_FALLBACK, isLoading } = useSWR<UserStats>(
		"/api/users/stats",
		userStatsFetcher,
		{
			revalidateOnFocus: false,
			revalidateOnReconnect: false,
		}
	);

	if (isLoading) {
		return (
			<div className="flex items-center justify-center gap-3">
				<div className="flex -space-x-2">
					{Array.from({ length: 4 }).map((_, i) => (
						<div
							aria-hidden="true"
							className={`size-9 animate-pulse rounded-full border-2 border-background bg-muted ${i > 0 ? "-ml-2" : ""}`}
							key={i}
						/>
					))}
				</div>
				<div
					aria-hidden="true"
					className="h-4 w-20 animate-pulse rounded bg-muted"
				/>
			</div>
		);
	}

	const userCount = stats.count ?? 0;
	const displayUsers = stats.users.slice(0, 4);
	const hasUsers = displayUsers.length > 0;

	if (!hasUsers && userCount === 0) {
		return null;
	}

	const formatUserCount = (count: number) => {
		if (count >= 1000) {
			return `${(count / 1000).toFixed(1)}k`;
		}
		return count.toString();
	};

	const getAvatarUrl = (index: number) => {
		return GRAVATAR_URLS[index % GRAVATAR_URLS.length];
	};

	const avatarCount = hasUsers
		? displayUsers.length
		: Math.min(4, userCount || 4);

	return (
		<div className="flex flex-wrap items-center justify-center gap-3">
			<div aria-label="User avatars" className="flex -space-x-2" role="group">
				{Array.from({ length: avatarCount }).map((_, index) => {
					const avatarUrl = getAvatarUrl(index);
					const user = hasUsers ? displayUsers[index] : null;
					const initials = user?.initials || "U";

					return (
						<Avatar
							aria-hidden={!user}
							aria-label={user ? "User avatar" : undefined}
							className={`size-9 border-2 border-background ${index > 0 ? "-ml-2" : ""}`}
							key={user ? `${initials}-${index}` : `avatar-${index}`}
						>
							<AvatarImage alt="User avatar" src={avatarUrl} />
							<AvatarFallback className="font-medium text-xs">
								{initials}
							</AvatarFallback>
						</Avatar>
					);
				})}
			</div>
			<div className="flex flex-col items-center gap-1 sm:items-start">
				<div
					aria-label="5 out of 5 stars rating"
					className="flex items-center gap-0.5"
				>
					{Array.from({ length: 5 }).map((_, i) => (
						<Star
							aria-hidden="true"
							className="size-4 fill-yellow-400 text-yellow-400"
							key={i}
						/>
					))}
				</div>
				{userCount > 0 && (
					<span className="font-medium text-muted-foreground text-sm tabular-nums">
						<span className="sr-only">Total users: </span> Loved by{" "}
						{formatUserCount(userCount)}+ users
					</span>
				)}
			</div>
		</div>
	);
}

function HeroCTAs() {
	const t = useTranslations("home.hero");
	return (
		<div
			aria-live="polite"
			className="flex w-fit flex-wrap items-center justify-center gap-3"
		>
			<Button
				asChild
				className="min-h-[2.75rem] rounded-full md:min-h-[2.75rem]"
				variant="default"
			>
				<Link
					className="flex h-11 w-full items-center justify-center gap-2 whitespace-nowrap font-medium md:w-auto md:min-w-[15rem]"
					href="/login"
				>
					<span>{t("ctaCreate")}</span>
					<ChevronRight aria-hidden="true" className="size-4" />
				</Link>
			</Button>
			<Button
				asChild
				className="min-h-[2.75rem] rounded-full md:min-h-[2.75rem]"
				variant="outline"
			>
				<Link
					className="flex h-11 w-full items-center justify-center gap-2 whitespace-nowrap font-medium md:w-auto md:min-w-[10rem]"
					href="/#form-builder-demo"
				>
					<span>{t("ctaDemo")}</span>
				</Link>
			</Button>
		</div>
	);
}

interface FormBuilderPreviewProps {
	onOpenFullscreen: () => void;
}

function FormBuilderPreview({ onOpenFullscreen }: FormBuilderPreviewProps) {
	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLDivElement>) => {
			if (e.key === "Enter" || e.key === " ") {
				e.preventDefault();
				onOpenFullscreen();
			}
		},
		[onOpenFullscreen]
	);

	return (
		<div
			aria-label="Open form builder demo in fullscreen (press Enter or Space)"
			className="group relative flex min-h-[2.75rem] w-full cursor-pointer items-center justify-center gap-3 bg-card p-4 transition-all hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 active:bg-muted/40 md:p-6"
			onClick={onOpenFullscreen}
			onKeyDown={handleKeyDown}
			role="button"
			tabIndex={0}
		>
			<div className="pointer-events-none absolute inset-0 flex items-center justify-center gap-2 rounded-lg bg-background/80 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
				<Maximize2 aria-hidden="true" className="size-5 text-foreground" />
				<span className="font-medium text-foreground">
					Click to view fullscreen
				</span>
			</div>
			<div className="pointer-events-none h-[56.25rem] w-full overflow-hidden">
				<DemoFormBuilder />
			</div>
		</div>
	);
}

export default function Hero() {
	const t = useTranslations("home.hero");
	const [isFormBuilderFullscreen, setIsFormBuilderFullscreen] = useState(false);

	const handleOpenFullscreen = useCallback(() => {
		setIsFormBuilderFullscreen(true);
	}, []);

	const handleCloseFullscreen = useCallback((open: boolean) => {
		setIsFormBuilderFullscreen(open);
	}, []);

	return (
		<>
			<section
				aria-labelledby="home-hero-title"
				className="mx-auto flex w-full max-w-7xl flex-col bg-linear-to-t from-10% from-background to-85% to-card"
			>
				<div className="relative z-20 flex h-full grow flex-col items-center gap-8 overflow-hidden border border-b-0 px-4 py-28 text-center md:px-6">
					<HeroHeading />
					<HeroSubheading />
					<HeroCTAs />
					<AvatarGroup />
				</div>

				<Card className="w-full max-w-7xl rounded-none border-b-0 bg-card shadow-none flex flex-col py-0">
					<Tabs
						className="w-full flex flex-col"
						defaultValue="form-builder-demo"
					>
						<div className="flex items-center justify-start border-border border-b px-4 py-4 md:px-6">
							<TabsList>
								<TabsTrigger value="form-builder-demo">
									{t("tabBuilder")}
								</TabsTrigger>
								<TabsTrigger value="form-demo">{t("tabForm")}</TabsTrigger>
							</TabsList>
						</div>
						<TabsContent className="mt-0" value="form-demo">
							<EmbeddedForm className="bg-card" />
						</TabsContent>
						<TabsContent
							className="mt-0"
							id="form-builder-demo"
							value="form-builder-demo"
						>
							<FormBuilderPreview onOpenFullscreen={handleOpenFullscreen} />
						</TabsContent>
					</Tabs>
				</Card>
			</section>

			<Dialog
				onOpenChange={handleCloseFullscreen}
				open={isFormBuilderFullscreen}
			>
				<DialogContent
					className="h-[95%] max-w-[95%] rounded-2xl p-0 sm:max-w-[95%]"
					showCloseButton={true}
				>
					<DialogTitle className="sr-only">Form Builder Demo</DialogTitle>
					<DemoFormBuilder />
				</DialogContent>
			</Dialog>
		</>
	);
}
